/**
 * BookingDocModal.jsx — Étapes de vérification documentaire avant réservation.
 *
 * Trois étapes obligatoires :
 *  1. Photos sur place du véhicule (1 à 4, caméra arrière)
 *  2. Permis de conduire recto (cadre CIN centré)
 *  3. Permis de conduire verso (cadre CIN centré)
 *
 * Bug caméra corrigé : srcObject est assigné via useEffect, après que le
 * <video> soit monté dans le DOM, et non directement dans openCamera().
 */

import { useEffect, useRef, useState } from 'react';
import { FiCamera, FiCheck, FiChevronRight, FiRefreshCw, FiTrash2, FiX } from 'react-icons/fi';

const STEPS = [
  {
    id: 'permis-recto',
    title: 'Permis de conduire — Recto',
    subtitle: 'Centrez le recto de votre permis de conduire dans le cadre. Toutes les informations doivent être lisibles.',
    instruction: 'Centrez le recto de votre permis dans le cadre',
    icon: '🪪',
    facing: 'environment',
    isCard: true,
    min: 1,
    max: 1,
  },
  {
    id: 'permis-verso',
    title: 'Permis de conduire — Verso',
    subtitle: 'Retournez votre permis et centrez le verso dans le cadre.',
    instruction: 'Centrez le verso de votre permis dans le cadre',
    icon: '🪪',
    facing: 'environment',
    isCard: true,
    min: 1,
    max: 1,
  },
];

/** Cadre de type CIN/carte bancaire superposé sur la vidéo */
function CardFrameOverlay({ maskId }) {
  const vw = 480, vh = 340;
  const cw = 400, ch = 252;
  const cx = (vw - cw) / 2;
  const cy = (vh - ch) / 2;
  const cl = 28, cs = 3;

  return (
    <svg
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      viewBox={`0 0 ${vw} ${vh}`}
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id={maskId}>
          <rect width={vw} height={vh} fill="white" />
          <rect x={cx} y={cy} width={cw} height={ch} rx="12" fill="black" />
        </mask>
      </defs>

      {/* Assombrissement hors cadre */}
      <rect width={vw} height={vh} fill="rgba(0,0,0,0.65)" mask={`url(#${maskId})`} />

      {/* Contour pointillé de la carte */}
      <rect x={cx} y={cy} width={cw} height={ch} rx="12"
        fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeDasharray="8 5" />

      {/* Coins — haut gauche */}
      <path d={`M${cx + cl} ${cy} L${cx} ${cy} L${cx} ${cy + cl}`}
        fill="none" stroke="#ffffff" strokeWidth={cs} strokeLinecap="round" />
      {/* Coins — haut droite */}
      <path d={`M${cx + cw - cl} ${cy} L${cx + cw} ${cy} L${cx + cw} ${cy + cl}`}
        fill="none" stroke="#ffffff" strokeWidth={cs} strokeLinecap="round" />
      {/* Coins — bas gauche */}
      <path d={`M${cx} ${cy + ch - cl} L${cx} ${cy + ch} L${cx + cl} ${cy + ch}`}
        fill="none" stroke="#ffffff" strokeWidth={cs} strokeLinecap="round" />
      {/* Coins — bas droite */}
      <path d={`M${cx + cw - cl} ${cy + ch} L${cx + cw} ${cy + ch} L${cx + cw} ${cy + ch - cl}`}
        fill="none" stroke="#ffffff" strokeWidth={cs} strokeLinecap="round" />
    </svg>
  );
}

export default function BookingDocModal({ onComplete, onClose }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [captured, setCaptured] = useState({
    'permis-recto': null,
    'permis-verso': null,
  });
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState('environment');

  const streamRef = useRef(null);
  const videoRef = useRef(null);

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  /* ── Correction du bug caméra ──────────────────────────────────────────────
   * L'élément <video> n'est monté dans le DOM qu'APRÈS setCameraOpen(true).
   * On ne peut donc pas assigner srcObject pendant openCamera() — la ref
   * est encore null à ce moment. Ce useEffect s'exécute après le rendu,
   * quand le <video> est garanti d'être dans le DOM.
   * ────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (cameraOpen && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraOpen]);

  /* Cleanup au démontage */
  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const openCamera = async () => {
    setCameraError('');
    stopStream(); // stoppe le stream précédent si existant

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('La capture photo n\'est pas prise en charge par ce navigateur.');
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = s;
      setCameraOpen(true); // <video> montera → useEffect assignera srcObject
    } catch {
      setCameraError('Impossible d\'accéder à la caméra. Vérifiez les permissions du navigateur.');
    }
  };

  const flipCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    if (!cameraOpen) return;
    stopStream();
    setCameraOpen(false);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: newMode }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = s;
      setCameraOpen(true);
    } catch {
      setCameraError('Impossible de basculer la caméra.');
    }
  };

  const closeCamera = () => {
    stopStream();
    setCameraOpen(false);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      setCameraError('La caméra n\'est pas encore prête, patientez 1 seconde.');
      return;
    }
    const w = video.videoWidth || 1280;
    const h = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);

    canvas.toBlob((blob) => {
      if (!blob) { setCameraError('Erreur lors de la capture, réessayez.'); return; }
      const file = new File([blob], `${step.id}-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(blob);
      setCaptured(prev => ({ ...prev, [step.id]: { file, previewUrl } }));
      closeCamera();
    }, 'image/jpeg', 0.92);
  };

  const removeCard = (stepId) => {
    setCaptured(prev => {
      if (prev[stepId]) URL.revokeObjectURL(prev[stepId].previewUrl);
      return { ...prev, [stepId]: null };
    });
  };

  const canProceed = () => captured[step.id] !== null;

  const handleNext = () => {
    if (!canProceed()) return;
    if (isLast) {
      onComplete({
        permisRecto: captured['permis-recto']?.file || null,
        permisVerso: captured['permis-verso']?.file || null,
      });
    } else {
      setStepIdx(i => i + 1);
    }
  };

  /* ── Styles inline partagés ── */
  const mono = { fontFamily: "'DM Mono', monospace" };
  const syne = { fontFamily: "'Syne', sans-serif" };

  return (
    /* Backdrop */
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(5, 10, 30, 0.9)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9998,
      backdropFilter: 'blur(8px)',
      padding: '1rem',
    }}>
      {/* Carte principale */}
      <div style={{
        width: '100%', maxWidth: 520,
        background: '#ffffff',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 40px 100px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '92vh',
      }}>

        {/* En-tête avec indicateurs d'étape */}
        <div style={{
          padding: '1rem 1.4rem 0.85rem',
          borderBottom: '1px solid #eef0f5',
          background: '#fafbfd',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            {STEPS.map((s, i) => (
              <div key={s.id} style={{
                height: 8,
                width: i === stepIdx ? 28 : (i < stepIdx ? 16 : 8),
                borderRadius: 4,
                background: i < stepIdx ? '#10b981' : i === stepIdx ? '#1a56f0' : '#dde2ee',
                transition: 'all 0.3s ease',
              }} />
            ))}
            <span style={{ ...mono, fontSize: '0.65rem', color: '#9aa0af', letterSpacing: '0.08em', marginLeft: 6 }}>
              {stepIdx + 1} / {STEPS.length}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#9aa0af', fontSize: '1.5rem', lineHeight: 1, padding: '0 0.2rem',
            display: 'flex', alignItems: 'center',
          }} aria-label="Fermer">
            <FiX size={20} />
          </button>
        </div>

        {/* Contenu scrollable */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* Info de l'étape */}
          <div style={{ padding: '1.1rem 1.4rem 0.9rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem' }}>
              <span style={{ fontSize: '1.6rem', lineHeight: 1, flexShrink: 0 }}>{step.icon}</span>
              <div>
                <div style={{ ...mono, fontSize: '0.62rem', color: '#1a56f0', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 }}>
                  Documents obligatoires
                </div>
                <div style={{ ...syne, fontSize: '1.05rem', fontWeight: 800, color: '#0a1a3f', marginBottom: 5, letterSpacing: '-0.01em' }}>
                  {step.title}
                </div>
                <p style={{ fontSize: '0.82rem', color: '#7a8599', margin: 0, lineHeight: 1.6 }}>
                  {step.subtitle}
                </p>
              </div>
            </div>
          </div>

          {/* Aperçu carte permis (steps recto / verso) */}
          {step.isCard && captured[step.id] && (
            <div style={{ padding: '0 1.4rem 0.9rem' }}>
              <div style={{
                position: 'relative', borderRadius: 12, overflow: 'hidden',
                border: '2.5px solid #10b981',
                boxShadow: '0 4px 16px rgba(16,185,129,0.2)',
              }}>
                <img src={captured[step.id].previewUrl} alt="Permis capturé"
                  style={{ width: '100%', height: 170, objectFit: 'cover', display: 'block' }} />
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.72))',
                  padding: '1.5rem 0.9rem 0.7rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <FiCheck size={14} color="#10b981" />
                    <span style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 600, ...syne }}>
                      Photo capturée
                    </span>
                  </div>
                  <button onClick={() => removeCard(step.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                    color: '#fff', fontSize: '0.72rem', fontWeight: 600, ...syne,
                  }}>
                    <FiTrash2 size={11} /> Reprendre
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bouton caméra */}
          {!captured[step.id] && (
            <div style={{ padding: '0 1.4rem 1.1rem' }}>
              <button onClick={openCamera} style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.65rem',
                padding: '0.9rem 1rem',
                background: 'linear-gradient(135deg, #1a56f0 0%, #0f3fd4 100%)',
                color: '#fff', border: 'none', borderRadius: 12, cursor: 'pointer',
                ...syne, fontSize: '0.9rem', fontWeight: 700,
                boxShadow: '0 6px 18px rgba(26,86,240,0.38)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 8px 22px rgba(26,86,240,0.45)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 6px 18px rgba(26,86,240,0.38)'; }}
              >
                <FiCamera size={18} />
                Ouvrir la caméra
              </button>
              {cameraError && (
                <p style={{ fontSize: '0.77rem', color: '#dc2626', marginTop: '0.5rem', textAlign: 'center', ...mono }}>
                  {cameraError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Pied de page avec navigation */}
        <div style={{
          padding: '0.9rem 1.4rem',
          borderTop: '1px solid #eef0f5',
          background: '#fafbfd',
          display: 'flex', gap: '0.6rem', justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <div>
            {stepIdx > 0 && (
              <button onClick={() => setStepIdx(i => i - 1)} style={{
                ...syne, padding: '0.65rem 1rem',
                background: 'none', border: '1.5px solid #dde2ee',
                borderRadius: 8, cursor: 'pointer', color: '#5a6478',
                fontSize: '0.82rem', fontWeight: 600,
              }}>
                ← Retour
              </button>
            )}
          </div>
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            style={{
              ...syne, padding: '0.7rem 1.5rem',
              background: canProceed()
                ? (isLast ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #1a56f0, #0f3fd4)')
                : '#e8ecf5',
              color: canProceed() ? '#fff' : '#aab0c0',
              border: 'none', borderRadius: 10,
              cursor: canProceed() ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 7,
              transition: 'all 0.18s ease',
              boxShadow: canProceed() ? '0 4px 14px rgba(0,0,0,0.18)' : 'none',
            }}
          >
            {isLast
              ? <><FiCheck size={16} /> Confirmer et payer</>
              : <>Étape suivante <FiChevronRight size={15} /></>}
          </button>
        </div>
      </div>

      {/* ── Caméra overlay ── */}
      {cameraOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0, 4, 18, 0.95)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(10px)',
          padding: '1rem',
        }}>
          <div style={{
            width: '100%', maxWidth: 490,
            background: '#0a0f1e',
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(0,0,0,0.7)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}>

            {/* Header caméra */}
            <div style={{
              padding: '0.9rem 1.2rem',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <span style={{ ...syne, color: '#e8f0ff', fontWeight: 700, fontSize: '0.9rem' }}>
                  {step.isCard ? '🪪 Cadrez votre permis' : '📸 Photo du véhicule'}
                </span>
                {step.isCard && (
                  <p style={{ ...mono, fontSize: '0.62rem', color: '#5a6a9a', margin: '2px 0 0', letterSpacing: '0.05em' }}>
                    {step.id === 'permis-recto' ? 'RECTO' : 'VERSO'} — assurez-vous que le texte est lisible
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button onClick={flipCamera} aria-label="Retourner caméra" title={facingMode === 'environment' ? 'Passer à la caméra avant' : 'Passer à la caméra arrière'} style={{
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: '#c0cce8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                }}>
                  <FiRefreshCw size={15} />
                </button>
                <button onClick={closeCamera} aria-label="Fermer caméra" style={{
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 8, width: 34, height: 34, cursor: 'pointer', color: '#c0cce8',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FiX size={16} />
                </button>
              </div>
            </div>

            {/* Flux vidéo + overlay */}
            <div style={{ position: 'relative', background: '#000', lineHeight: 0 }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', display: 'block', maxHeight: 340, objectFit: 'cover' }}
              />
              {/* Cadre CIN uniquement pour les étapes permis */}
              {step.isCard && <CardFrameOverlay maskId={`cin-${step.id}`} />}
              {/* Instruction texte */}
              <div style={{
                position: 'absolute', bottom: 14, left: 0, right: 0,
                display: 'flex', justifyContent: 'center', pointerEvents: 'none',
              }}>
                <span style={{
                  background: 'rgba(10, 26, 79, 0.85)', color: '#fff',
                  fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em',
                  padding: '5px 18px', borderRadius: 20,
                  ...mono,
                }}>
                  {step.instruction}
                </span>
              </div>
            </div>

            {/* Footer caméra */}
            <div style={{
              padding: '1rem 1.2rem',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', gap: '0.7rem', justifyContent: 'flex-end',
            }}>
              <button onClick={closeCamera} style={{
                ...syne, padding: '0.62rem 1.1rem',
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: '#c0cce8', cursor: 'pointer',
                fontSize: '0.82rem', fontWeight: 600,
              }}>
                Annuler
              </button>
              <button onClick={handleCapture} style={{
                ...syne, padding: '0.62rem 1.4rem',
                background: 'linear-gradient(135deg, #1a56f0, #0f3fd4)',
                border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 7,
                boxShadow: '0 4px 16px rgba(26,86,240,0.45)',
              }}>
                <FiCamera size={15} /> Capturer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
